import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import ParametroCertificadoOperadorService, { ParametroCertificadoOperador } from '../../../../services/parametroCertificadoOperadorService';

const parametroCertificadoService = new ParametroCertificadoOperadorService();

export interface CertificadoOperadorCardRef {
  salvarSelecaoCertificados: () => Promise<void>;
}

const CertificadoOperadorCard = forwardRef<CertificadoOperadorCardRef>(function CertificadoOperadorCard(_, ref) {
  const [parametrosCertificado, setParametrosCertificado] = useState<ParametroCertificadoOperador[]>([]);
  const [tiposSelecionados, setTiposSelecionados] = useState<Set<string>>(new Set());
  const [mensagem, setMensagem] = useState<{ texto: string, tipo: 'success' | 'error' } | null>(null);

  const listarParametrosCertificado = useCallback(async () => {
    try {
      const response = await parametroCertificadoService.listarTodos();
      const dados = response.data || [];
      setParametrosCertificado(dados);
      const ativos = new Set<string>(dados.filter((p: ParametroCertificadoOperador) => p.ativo !== false).map((p: ParametroCertificadoOperador) => p.tipoCertificado));
      setTiposSelecionados(ativos);
    } catch (err: any) {
      setMensagem({ texto: err.response?.data || 'Erro ao listar parâmetros de certificado.', tipo: 'error' });
    }
  }, []);

  useEffect(() => {
    const carregarCertificados = async () => {
      try {
        await parametroCertificadoService.sincronizarTipos();
      } catch (err: any) {
        // tipos já existem ou nenhum operador com certificado
      } finally {
        listarParametrosCertificado();
      }
    };
    carregarCertificados();
  }, [listarParametrosCertificado]);

  const toggleTipoSelecionado = (tipo: string) => {
    setTiposSelecionados(prev => {
      const novo = new Set(prev);
      if (novo.has(tipo)) {
        novo.delete(tipo);
      } else {
        novo.add(tipo);
      }
      return novo;
    });
  };

  const salvarSelecaoCertificados = async () => {
    try {
      for (const param of parametrosCertificado) {
        const deveEstarAtivo = tiposSelecionados.has(param.tipoCertificado);
        if (param.ativo !== deveEstarAtivo) {
          await parametroCertificadoService.salvar({
            ...param,
            ativo: deveEstarAtivo
          });
        }
      }
      setMensagem({ texto: 'Seleção de certificados salva com sucesso!', tipo: 'success' });
      listarParametrosCertificado();
    } catch (err: any) {
      setMensagem({ texto: err.response?.data || 'Erro ao salvar seleção.', tipo: 'error' });
      throw err;
    }
  };

  useImperativeHandle(ref, () => ({
    salvarSelecaoCertificados
  }));

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">Tipos de Certificado do Operador</h5>
      </div>
      <div className="card-body">
        <p className="text-muted small">Selecione os tipos de certificado que serão permitidos no cadastro de operadores:</p>

        {mensagem && (
          <div className={`alert alert-${mensagem.tipo === 'success' ? 'success' : 'danger'} alert-dismissible`}>
            {mensagem.texto}
            <button type="button" className="btn-close" onClick={() => setMensagem(null)} />
          </div>
        )}

        <table className="table table-striped table-bordered table-hover table-sm">
          <thead>
            <tr style={{ backgroundColor: '#24393A', color: 'white' }}>
              <th style={{ width: '60px', textAlign: 'center' }}>Selecionar</th>
              <th style={{ width: '200px' }}>Tipo de Certificado</th>
              <th>Nome do Certificado</th>
            </tr>
          </thead>
          <tbody>
            {parametrosCertificado.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center' }}>
                  Nenhum certificado parametrizado. Os tipos serão carregados automaticamente dos operadores cadastrados.
                </td>
              </tr>
            ) : (
              parametrosCertificado.map((p) => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => toggleTipoSelecionado(p.tipoCertificado)}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label={`Selecionar ${p.tipoCertificado}`}
                      checked={tiposSelecionados.has(p.tipoCertificado)}
                      onChange={() => toggleTipoSelecionado(p.tipoCertificado)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>{p.tipoCertificado}</td>
                  <td>{p.descricao || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {parametrosCertificado.length > 0 && (
          <span className="text-muted small">
            {tiposSelecionados.size} tipo(s) selecionado(s)
          </span>
        )}
      </div>
    </div>
  );
});

export default CertificadoOperadorCard;
